import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Activity,
    AlertTriangle,
    CalendarDays,
    ChevronLeft,
    ChevronRight,
    ClipboardCheck,
    Clock,
    ExternalLink,
    MapPin,
    Store,
    UserCheck,
    Users,
    X,
} from "lucide-react";
import { toast } from "react-hot-toast";

import api from "../../api/axios";
import AdminNavbar from "../../components/layout/AdminNavbar";
import ReverseGeocode from "../../components/ReverseGeocode";
import LiveTrackingMap from "../../components/LiveTrackingMap";
import { formatAttendanceRecordTime } from "../../utils/attendanceTime";

const todayKey = () => {
    const value = new Date();
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const formatTeamName = (value) => {
    const team = String(value || "").trim();
    if (!team) return "-";
    return `${team.charAt(0).toUpperCase()}${team.slice(1)}`;
};

const formatRoleLabel = (emp = {}) => {
    const designation = String(emp.designation || "").trim();
    if (designation) return designation;
    const role = String(emp.role || "").trim().toLowerCase();
    if (role === "lead" || role === "team_lead") return "Sales Team Lead";
    if (role === "hr") return "Sales HR";
    if (role === "admin") return "Sales Admin";
    return "Sales Executive";
};

const renderRoleBadge = (emp = {}) => {
    const designation = String(emp.designation || "").trim();
    const role = String(emp.role || "").trim().toLowerCase();
    const isLead = role === "lead" || role === "team_lead" || designation.toLowerCase().includes("lead");

    if (isLead) {
        return (
            <span className="inline-flex items-center rounded-md bg-[var(--accent-subtle)] px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-[var(--accent-primary)] dark:text-[var(--accent-primary)] border border-[var(--accent-primary)]/20 shrink-0">
                Team Lead
            </span>
        );
    }
    return (
        <span className="inline-flex items-center rounded-md bg-[var(--accent-primary)]/10 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-[var(--accent-primary)] dark:text-[var(--accent-primary)] border border-[var(--accent-primary)]/20 shrink-0">
            Executive
        </span>
    );
};

const formatInr = (value) =>
    Number(value || 0).toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
    });

const formatDistance = (meters) => {
    const value = Number(meters || 0);
    if (value >= 1000) return `${(value / 1000).toFixed(1)} km`;
    return `${Math.round(value)} m`;
};

const minutesAgo = (value) => {
    if (!value) return "No ping yet";
    const diff = Math.max(0, Date.now() - new Date(value).getTime());
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "Just now";
    if (minutes === 1) return "1 min ago";
    return `${minutes} mins ago`;
};

const trackingTone = {
    tracking: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
    checked_in: "bg-[var(--accent-primary)]/10 text-[var(--heading-secondary)] border-[var(--accent-primary)]/20",
    checked_out: "bg-neutral-500/10 text-neutral-600 border-neutral-500/20",
    not_checked_in: "bg-amber-500/10 text-amber-700 border-amber-500/20",
};

const trackingLabel = {
    tracking: "Live",
    checked_in: "Checked in",
    checked_out: "Checked out",
    not_checked_in: "Not in",
};

const SalesLiveTracking = () => {
    const [date, setDate] = useState(todayKey());
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState({
        totalSalesEmployees: 0,
        activeTracking: 0,
        checkedIn: 0,
        fieldTotals: {},
        alerts: [],
        employees: [],
    });
    const [selectedId, setSelectedId] = useState("");
    const [galleryModal, setGalleryModal] = useState({
        isOpen: false,
        employeeName: "",
        photos: [],
        currentIndex: 0,
    });

    const fetchTracking = useCallback(async (quiet = false) => {
        if (!quiet) {
            setLoading(true);
        }

        try {
            const res = await api.get("/api/admin/sales-live-tracking", {
                params: {
                    date,
                },
                cacheTtlMs: 30000,
            });
            const data = res.data || {};
            const employees = Array.isArray(data.employees) ? data.employees : [];
            setSummary({
                totalSalesEmployees: data.totalSalesEmployees || employees.length,
                activeTracking: data.activeTracking || 0,
                checkedIn: data.checkedIn || 0,
                fieldTotals: data.fieldTotals || {},
                alerts: Array.isArray(data.alerts) ? data.alerts : [],
                employees,
            });
            setSelectedId((current) => {
                if (current && employees.some((item) => item.employee?._id === current)) return current;
                return employees.find((item) => item.liveTracking?.lastLocation)?.employee?._id || employees[0]?.employee?._id || "";
            });
        } catch (err) {
            console.error("Failed to load sales live tracking:", err);
            toast.error(err.response?.data?.message || "Failed to load sales live tracking.");
        } finally {
            setLoading(false);
        }
    }, [date]);

    useEffect(() => {
        document.title = "Sales Tracking | Savrion Technologies";
        fetchTracking(false);
    }, [fetchTracking]);

    useEffect(() => {
        document.title = "Sales Tracking | Savrion Technologies";
        const intervalId = window.setInterval(() => {
            fetchTracking(true);
        }, 30000);

        return () => window.clearInterval(intervalId);
    }, [fetchTracking]);

    const selectedEmployee = useMemo(
        () => summary.employees.find((item) => item.employee?._id === selectedId) || summary.employees[0],
        [summary.employees, selectedId],
    );
    const selectedLocation = selectedEmployee?.liveTracking?.lastLocation;
    const activeRows = summary.employees.filter((item) => item.liveTracking?.status === "tracking");
    const fieldTotals = summary.fieldTotals || {};

    return (
        <div className="flex min-h-screen flex-col bg-[var(--bg-secondary)] text-[var(--text-primary)] xl:flex-row">
            <AdminNavbar />
            <main className="min-h-screen flex-1 min-w-0 overflow-x-hidden p-3 sm:p-4 lg:p-5">
                <section className="mx-auto w-full max-w-[1500px] space-y-4">
                    <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-[var(--shadow-soft)]">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                                <span className="text-[11px] font-black uppercase tracking-[0.22em] text-[var(--accent-primary)]">
                                    Sales live tracking
                                </span>
                                <h1 className="mt-1 text-2xl font-black tracking-tight md:text-[28px]">
                                    Field sales command dashboard
                                </h1>
                                <p className="mt-1 text-sm font-semibold text-[var(--text-secondary)]">
                                    Route tracking, shop visit proof, EOD outcomes, quotations, deals, and field alerts.
                                </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <label className="inline-flex h-11 items-center gap-2 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm font-bold">
                                    <CalendarDays size={16} />
                                    <input
                                        type="date"
                                        value={date}
                                        onChange={(event) => setDate(event.target.value)}
                                        className="bg-transparent outline-none"
                                    />
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                        <MetricCard icon={<Users size={18} />} label="Sales Employees" value={summary.totalSalesEmployees} />
                        <MetricCard icon={<Activity size={18} />} label="Live Now" value={summary.activeTracking} tone="emerald" />
                        <MetricCard icon={<UserCheck size={18} />} label="Checked In" value={summary.checkedIn} tone="blue" />
                        <MetricCard icon={<Store size={18} />} label="Shops Visited" value={fieldTotals.shopsVisited || 0} tone="violet" />
                    </div>

                    <div className="hidden">
                        <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-[var(--shadow-soft)]">
                            <div className="mb-4 flex items-center justify-between gap-3">
                                <div>
                                    <h2 className="text-lg font-black">Field visit verification</h2>
                                    <p className="text-xs font-semibold text-[var(--text-secondary)]">
                                        Based on submitted Sales EOD, camera proof, GPS photos, and route distance.
                                    </p>
                                </div>
                                <ClipboardCheck size={20} className="text-[var(--accent-primary)]" />
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[620px] table-fixed text-left text-xs 2xl:text-sm">
                                    <colgroup>
                                        <col className="w-[25%]" /><col className="w-[17%]" /><col className="w-[12%]" />
                                        <col className="w-[14%]" /><col className="w-[15%]" /><col className="w-[17%]" />
                                    </colgroup>
                                    <thead className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)]">
                                        <tr className="border-b border-[var(--border-color)]">
                                            <th className="py-2 pr-2">Salesperson</th>
                                            <th>EOD</th>
                                            <th>Shops</th>
                                            <th>Photos</th>
                                            <th>Route</th>
                                            <th className="text-right">Revenue</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {summary.employees.map((item) => {
                                            const employee = item.employee || {};
                                            const report = item.fieldReport || {};
                                            const metrics = report.metrics || {};
                                            return (
                                                <tr key={employee._id} className="border-b border-[var(--border-color)]/70 font-bold">
                                                    <td className="py-3">
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            <span className="font-black">{employee.name}</span>
                                                            {renderRoleBadge(employee)}
                                                        </div>
                                                        <div className="text-xs text-[var(--text-secondary)] font-medium mt-0.5">
                                                            {employee.employeeId || "-"} • {formatRoleLabel(employee)}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={`rounded-full px-2 py-1 text-[10px] font-black uppercase ${report.submitted ? "bg-emerald-500/10 text-emerald-700" : "bg-amber-500/10 text-amber-700"}`}>
                                                            {report.submitted ? "Submitted" : "Pending"}
                                                        </span>
                                                    </td>
                                                    <td>{report.shopsVisited || 0}</td>
                                                    <td>{report.gpsPhotoCount || 0}/{report.photoCount || 0}</td>
                                                    <td>{formatDistance(report.routeDistanceMeters)}</td>
                                                    <td className="text-right">{formatInr(metrics.revenue)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </section>

                        <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-[var(--shadow-soft)]">
                            <div className="mb-4 flex items-center justify-between gap-3">
                                <div>
                                    <h2 className="text-lg font-black">Smart field alerts</h2>
                                    <p className="text-xs font-semibold text-[var(--text-secondary)]">
                                        Missing proof, no tracking, and incomplete visit notes.
                                    </p>
                                </div>
                                <AlertTriangle size={20} className="text-amber-600" />
                            </div>
                            <div className="max-h-[320px] overflow-y-auto rounded-xl border border-[var(--border-color)]">
                                {summary.alerts.length > 0 && (
                                    <div className="grid grid-cols-[1fr_auto] bg-[var(--bg-primary)] px-3 py-2 text-[9px] font-black uppercase tracking-wider text-[var(--text-secondary)]">
                                        <span>Alert / Detail</span><span>Level</span>
                                    </div>
                                )}
                                {summary.alerts.length ? summary.alerts.map((alert, index) => (
                                    <div key={`${alert.title}-${index}`} className="grid grid-cols-[1fr_auto] gap-3 border-t border-[var(--border-color)] bg-amber-50/70 px-3 py-3">
                                        <div className="flex items-center justify-between gap-2">
                                            <div><p className="text-xs font-black text-amber-950">{alert.title}</p><p className="mt-1 text-[11px] font-bold text-amber-800">{alert.detail}</p></div>
                                        </div>
                                        <span className="h-fit rounded-full bg-[var(--card-bg)] px-2 py-1 text-[9px] font-black uppercase text-amber-700">{alert.level}</span>
                                    </div>
                                )) : (
                                    <div className="bg-emerald-500/10 px-3 py-4 text-sm font-black text-emerald-700">
                                        No field verification alerts for this date.
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>

                    <section className="overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-[var(--shadow-soft)]">
                        <div className="flex items-center justify-between border-b border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3">
                            <div><h2 className="text-sm font-black">Attention needed</h2><p className="text-xs font-semibold text-[var(--text-secondary)]">Current field exceptions for the selected date.</p></div>
                            <span className={`rounded-full px-3 py-1 text-xs font-black ${summary.alerts.length ? "bg-amber-500/10 text-amber-700" : "bg-emerald-500/10 text-emerald-700"}`}>{summary.alerts.length} alerts</span>
                        </div>
                        {summary.alerts.length > 0 && (
                            <div className="divide-y divide-[var(--border-color)]">
                                {summary.alerts.slice(0, 3).map((alert, index) => (
                                    <div key={`${alert.title}-${index}`} className="grid gap-1 px-4 py-3 sm:grid-cols-[180px_1fr_auto] sm:items-center sm:gap-4">
                                        <strong className="text-xs">{alert.title}</strong><span className="text-xs font-semibold text-[var(--text-secondary)]">{alert.detail}</span><span className="text-[9px] font-black uppercase text-amber-700">{alert.level}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    {/* LIVE SALES TEAM MAP */}
                    <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-[var(--shadow-soft)] space-y-3">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <h2 className="text-lg font-black flex items-center gap-2">
                                    <MapPin className="text-emerald-500" size={20} />
                                    <span>Sales Team Live Location Map</span>
                                </h2>
                                <p className="text-xs font-semibold text-[var(--text-secondary)]">
                                    Touch any employee marker on the map to see their exact spot, role, check-in time, and visit details.
                                </p>
                            </div>
                            <span className="rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-black text-emerald-700">
                                {summary.employees.filter(e => e.liveTracking?.lastLocation?.latitude).length} mapped
                            </span>
                        </div>
                        <LiveTrackingMap
                            employees={summary.employees}
                            selectedId={selectedId}
                            onSelectEmployee={(id) => setSelectedId(id)}
                        />
                    </section>

                    <div className="flex flex-col gap-4">
                        <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-[var(--shadow-soft)]">
                            <div className="mb-4 flex items-center justify-between gap-3">
                                <div>
                                    <h2 className="text-lg font-black">Sales operations</h2>
                                    <p className="text-xs font-semibold text-[var(--text-secondary)]">
                                        Auto-refreshes every 30 seconds. Click any shop proof to inspect visit photos.
                                    </p>
                                </div>
                                <span className="rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-black text-emerald-700">
                                    {activeRows.length} active
                                </span>
                            </div>

                            {loading ? (
                                <div className="rounded-xl border border-dashed border-[var(--border-color)] p-8 text-center text-sm font-bold text-[var(--text-secondary)]">
                                    Loading sales locations...
                                </div>
                            ) : summary.employees.length === 0 ? (
                                <div className="rounded-xl border border-dashed border-[var(--border-color)] p-8 text-center text-sm font-bold text-[var(--text-secondary)]">
                                    No sales team employees found.
                                </div>
                            ) : (
                                <div className="overflow-x-auto rounded-xl border border-[var(--border-color)]">
                                    <table className="w-full text-left text-xs">
                                        <colgroup>
                                            <col className="w-[30%]" /><col className="w-[16%]" /><col className="w-[16%]" /><col className="w-[12%]" /><col className="w-[26%]" />
                                        </colgroup>
                                        <thead className="bg-[var(--bg-primary)] text-[10px] font-black uppercase tracking-wider text-[var(--text-secondary)] border-b border-[var(--border-color)]">
                                            <tr>
                                                <th className="px-3 py-3 border-r border-[var(--border-color)]">Employee</th>
                                                <th className="px-3 py-3 border-r border-[var(--border-color)]">Check in</th>
                                                <th className="px-3 py-3 border-r border-[var(--border-color)]">Check out</th>
                                                <th className="px-3 py-3 text-center border-r border-[var(--border-color)]">Shops</th>
                                                <th className="px-3 py-3 text-center">Shop Proofs</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {summary.employees.map((item) => {
                                                const employee = item.employee || {};
                                                const live = item.liveTracking || {};
                                                const report = item.fieldReport || {};
                                                const photos = report.photos || [];
                                                const allPhotos = photos.length > 0
                                                    ? photos
                                                    : (item.attendance?.salesCheckInProof?.photoData ? [{
                                                        data: item.attendance.salesCheckInProof.photoData,
                                                        location: item.attendance.salesCheckInProof.location,
                                                        locationName: item.attendance.salesCheckInProof.locationName,
                                                        description: item.attendance.salesCheckInProof.description,
                                                        uploadedAt: item.attendance.salesCheckInProof.capturedAt,
                                                    }] : []);
                                                const totalShops = Number(report.shopsVisited || allPhotos.length || 0);
                                                const selected = employee._id === selectedId;
                                                
                                                return (
                                                    <tr
                                                        key={employee._id}
                                                        tabIndex={0}
                                                        onClick={() => setSelectedId(employee._id)}
                                                        onKeyDown={(event) => event.key === "Enter" && setSelectedId(employee._id)}
                                                        className={`cursor-pointer border-t border-[var(--border-color)] transition hover:bg-[var(--accent-primary)]/5 ${selected ? "bg-[var(--accent-primary)]/10" : "bg-[var(--card-bg)]"}`}
                                                    >
                                                        <td className="px-3 py-3 border-r border-[var(--border-color)]">
                                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                                <strong className="truncate text-sm font-black">{employee.name}</strong>
                                                                {renderRoleBadge(employee)}
                                                            </div>
                                                            <span className="text-[10px] font-bold text-[var(--text-secondary)]">
                                                                {employee.employeeId || "-"} • {formatTeamName(employee.team)} • {formatRoleLabel(employee)}
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-3 font-bold border-r border-[var(--border-color)]">{formatAttendanceRecordTime(item.attendance || {}, "checkIn", "-")}</td>
                                                        <td className="px-3 py-3 font-bold border-r border-[var(--border-color)]">{formatAttendanceRecordTime(item.attendance || {}, "checkOut", "-")}</td>
                                                        <td className="px-3 py-3 text-center font-black border-r border-[var(--border-color)]">{totalShops}</td>
                                                        <td className="px-3 py-3">
                                                            {allPhotos.length > 0 ? (
                                                                <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                                                    {allPhotos.map((photo, pIdx) => {
                                                                        const imgSrc = photo.data || photo.photoData;
                                                                        if (!imgSrc) return null;
                                                                        return (
                                                                            <button
                                                                                key={pIdx}
                                                                                type="button"
                                                                                title={`Shop #${pIdx + 1}: ${photo.locationName || "View proof"}`}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    setGalleryModal({
                                                                                        isOpen: true,
                                                                                        employeeName: employee.name,
                                                                                        photos: allPhotos,
                                                                                        currentIndex: pIdx,
                                                                                    });
                                                                                }}
                                                                                className="relative group h-9 w-9 overflow-hidden rounded-lg border border-[var(--border-color)] transition hover:border-[var(--accent-primary)] hover:scale-110 focus:outline-none shadow-sm flex-shrink-0"
                                                                            >
                                                                                <img src={imgSrc} alt={`Shop ${pIdx + 1}`} className="h-full w-full object-cover" />
                                                                                <span className="absolute bottom-0 right-0 bg-black/75 text-[9px] font-black text-white px-1 rounded-tl leading-none py-0.5">
                                                                                    #{pIdx + 1}
                                                                                </span>
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ) : (
                                                                <div className="text-center">
                                                                    <span className="text-[9px] font-black uppercase text-[var(--text-secondary)]">No photo</span>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    </div>
                </section>
                
                {galleryModal.isOpen && galleryModal.photos.length > 0 && (() => {
                    const currentPhoto = galleryModal.photos[galleryModal.currentIndex] || galleryModal.photos[0];
                    const photoSrc = currentPhoto.data || currentPhoto.photoData;
                    const hasPrev = galleryModal.currentIndex > 0;
                    const hasNext = galleryModal.currentIndex < galleryModal.photos.length - 1;

                    return (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200" onClick={() => setGalleryModal({ isOpen: false, employeeName: "", photos: [], currentIndex: 0 })}>
                            <div className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-[var(--card-bg)] border border-[var(--border-color)] shadow-2xl" onClick={(e) => e.stopPropagation()}>
                                {/* Header */}
                                <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--border-color)] bg-[var(--bg-secondary)]">
                                    <div>
                                        <h3 className="text-sm font-black text-[var(--text-primary)]">
                                            {galleryModal.employeeName} — Shop Visits ({galleryModal.currentIndex + 1} of {galleryModal.photos.length})
                                        </h3>
                                        <p className="text-[11px] text-[var(--text-secondary)] font-medium">Captured on {date}</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setGalleryModal({ isOpen: false, employeeName: "", photos: [], currentIndex: 0 })}
                                        className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--border-color)] transition focus:outline-none"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>

                                {/* Main Image View with Next/Prev controls */}
                                <div className="relative flex items-center justify-center bg-black/95 p-4 min-h-[300px] max-h-[50vh]">
                                    {hasPrev && (
                                        <button
                                            type="button"
                                            onClick={() => setGalleryModal((prev) => ({ ...prev, currentIndex: prev.currentIndex - 1 }))}
                                            className="absolute left-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur hover:bg-black/90 transition shadow-lg"
                                        >
                                            <ChevronLeft size={20} />
                                        </button>
                                    )}

                                    <img
                                        src={photoSrc}
                                        alt={`Shop ${galleryModal.currentIndex + 1}`}
                                        className="max-h-[45vh] w-auto max-w-full rounded-xl object-contain shadow-2xl"
                                    />

                                    {hasNext && (
                                        <button
                                            type="button"
                                            onClick={() => setGalleryModal((prev) => ({ ...prev, currentIndex: prev.currentIndex + 1 }))}
                                            className="absolute right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur hover:bg-black/90 transition shadow-lg"
                                        >
                                            <ChevronRight size={20} />
                                        </button>
                                    )}
                                </div>

                                {/* Details & Info Footer */}
                                <div className="p-4 space-y-3 bg-[var(--card-bg)] max-h-[32vh] overflow-y-auto">
                                    {/* Location & Time info box */}
                                    <div className="flex items-start gap-2.5 text-xs text-[var(--text-secondary)] bg-[var(--bg-secondary)] px-3.5 py-2.5 rounded-xl border border-[var(--border-color)]">
                                        <MapPin size={15} className="flex-shrink-0 mt-0.5 text-rose-500" />
                                        <div className="flex-1 space-y-1">
                                            <div className="font-bold text-sm text-[var(--text-primary)]">
                                                {currentPhoto.locationName || `Shop #${galleryModal.currentIndex + 1}`}
                                            </div>
                                            <div className="text-xs text-[var(--text-secondary)] leading-snug">
                                                <ReverseGeocode latitude={currentPhoto.location?.latitude} longitude={currentPhoto.location?.longitude} showGps={false} />
                                            </div>
                                            <div className="flex items-center gap-3 pt-0.5 text-[11px] font-medium text-[var(--text-tertiary)]">
                                                {(currentPhoto.uploadedAt || currentPhoto.capturedAt) && (
                                                    <span className="flex items-center gap-1 font-bold text-amber-600 dark:text-amber-400">
                                                        <Clock size={12} className="text-amber-500" />
                                                        Time: {new Date(currentPhoto.uploadedAt || currentPhoto.capturedAt).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                                                    </span>
                                                )}
                                                {currentPhoto.location?.latitude && (
                                                    <a
                                                        href={`https://www.google.com/maps?q=${currentPhoto.location.latitude},${currentPhoto.location.longitude}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="font-mono opacity-80 hover:opacity-100 hover:text-rose-500 hover:underline transition-all inline-flex items-center gap-1 cursor-pointer"
                                                        title="Open location in Google Maps"
                                                    >
                                                        <span>GPS: {Number(currentPhoto.location.latitude).toFixed(5)}, {Number(currentPhoto.location.longitude).toFixed(5)}</span>
                                                        <ExternalLink size={10} className="text-rose-500" />
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Description Notes */}
                                    {currentPhoto.description && (
                                        <div className="p-3 rounded-xl bg-[var(--bg-primary)] border border-[var(--border-color)] text-xs text-[var(--text-primary)] leading-relaxed whitespace-pre-wrap">
                                            <strong>Notes:</strong> {currentPhoto.description}
                                        </div>
                                    )}

                                    {/* Thumbnail strip for multiple photos */}
                                    {galleryModal.photos.length > 1 && (
                                        <div className="flex items-center gap-2 pt-1 overflow-x-auto pb-1">
                                            {galleryModal.photos.map((p, idx) => (
                                                <button
                                                    key={idx}
                                                    type="button"
                                                    onClick={() => setGalleryModal((prev) => ({ ...prev, currentIndex: idx }))}
                                                    className={`relative h-12 w-12 flex-shrink-0 rounded-lg overflow-hidden border-2 transition ${
                                                        idx === galleryModal.currentIndex
                                                            ? "border-[var(--accent-primary)] ring-2 ring-[var(--accent-primary)]/30 scale-105"
                                                            : "border-[var(--border-color)] opacity-60 hover:opacity-100"
                                                    }`}
                                                >
                                                    <img src={p.data || p.photoData} alt={`Thumbnail ${idx + 1}`} className="h-full w-full object-cover" />
                                                    <span className="absolute bottom-0 right-0 bg-black/80 text-[8px] font-black text-white px-1 py-0.2 rounded-tl leading-none">
                                                        #{idx + 1}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </main>
        </div>
    );
};

const MetricCard = ({ icon, label, value, tone = "violet" }) => {
    const colors = {
        violet: "bg-[var(--accent-subtle)] text-[var(--heading-secondary)]",
        emerald: "bg-emerald-500/10 text-emerald-700",
        blue: "bg-[var(--accent-primary)]/10 text-[var(--heading-secondary)]",
        amber: "bg-amber-500/10 text-amber-700",
    };

    return (
        <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-[var(--shadow-soft)]">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)]">{label}</p>
                    <h2 className="mt-2 text-2xl font-black">{value}</h2>
                </div>
                <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${colors[tone] || colors.violet}`}>
                    {icon}
                </span>
            </div>
        </div>
    );
};


export default SalesLiveTracking;


